//  SubmissionDetail  -  View  and  manage  a  single  submission
//  Shows  receipts,  allows  adding  new  ones,  and  submitting  for  approval

import  React,  {  useState,  useEffect  }  from  'react';
import  {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    Grid,
    Alert,
    CircularProgress,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
}  from  '@mui/material';
import  {
    ArrowBack  as  BackIcon,
    Send  as  SendIcon,
    Add  as  AddIcon,
    Warning  as  WarningIcon,
    Info  as  InfoIcon,
    CheckCircle  as  CheckIcon,
    Cancel  as  RejectIcon,
}  from  '@mui/icons-material';
import  {  formatSubmissionPeriod  }  from  '../../utils/dateHelpers';
import  {  useToast  }  from  '../../contexts/ToastContext';
import  {  useAuth  }  from  '../../contexts/AuthContext';
import  {  useNavigate,  useParams  }  from  'react-router-dom';
import  {  submissionsAPI,  receiptsAPI  }  from  '../../services/api';
import  {  getStatusColor,  getStatusLabel  }  from  '../../utils/statusHelpers';
import  type  {  Submission,  Receipt  }  from  '../../types';
import  dayjs  from  'dayjs';
import  ReceiptCard  from  '../receipts/ReceiptCard';
import  AddReceiptDialog  from  '../receipts/AddReceiptDialog';

const  SubmissionDetail:  React.FC  =  ()  =>  {
    const  {  id  }  =  useParams<{  id:  string  }>();
    const  navigate  =  useNavigate();
    const  {  showSuccess,  showError  }  =  useToast();
    const  {  user  }  =  useAuth();
    
    //  State
    const  [submission,  setSubmission]  =  useState<Submission  |  null>(null);
    const  [receipts,  setReceipts]  =  useState<Receipt[]>([]);
    const  [programCategories,  setProgramCategories]  =  useState<string[]>([]);
    const  [loading,  setLoading]  =  useState(true);
    const  [error,  setError]  =  useState<string  |  null>(null);

    //  Dialog  states
    const  [addReceiptOpen,  setAddReceiptOpen]  =  useState(false);
    const  [submitDialogOpen,  setSubmitDialogOpen]  =  useState(false);
    const  [signature,  setSignature]  =  useState('');
    const  [submitting,  setSubmitting]  =  useState(false);

    useEffect(()  =>  {
        if  (id)  {
            fetchSubmissionData();
        }
    },  [id]);

    //  ========================================
    //  DATA  FETCHING
    //  ========================================

    const  fetchSubmissionData  =  async  ()  =>  {
        if  (!id)  return;

        try  {
            setLoading(true);
            setError(null);
            
            const  [submissionData,  receiptsData]  =  await  Promise.all([
                submissionsAPI.getSubmission(id),
                receiptsAPI.getReceipts(id),
            ]);

            setSubmission(submissionData);
            setReceipts(receiptsData  ||  []);
            
            //  Extract  and  store  expense  categories  from  program
            //  Extract  and  store  expense  categories  from  program
//  Extract  and  store  expense  categories  from  program
if  (submissionData.programCategories  &&  submissionData.programCategories.length  >  0)  {
    setProgramCategories(submissionData.programCategories);
    console.log('🔋  Program  categories  loaded:',  submissionData.programCategories);
}  else  {
    //  Fallback  to  default  categories  if  none  specified
    console.warn('⚠️  No  categories  found  in  program,  using  defaults');
    setProgramCategories([
        'Travel',
        'Meals',
        'Office  Supplies',
        'Equipment',
        'Training',
        'Professional  Development',
        'Software/Subscriptions',
        'Other'
    ]);
}
        }  catch  (err:  any)  {
            setError(err.response?.data?.message  ||  'Failed  to  load  submission');
            console.error('Error  fetching  submission:',  err);
        }  finally  {
            setLoading(false);
        }
    };

    //  ========================================
    //  HANDLERS
    //  ========================================

    const  handleReceiptAdded  =  (newReceipt:  Receipt)  =>  {
        setReceipts([...receipts,  newReceipt]);
        setAddReceiptOpen(false);
        
        if  (id)  {
            submissionsAPI.getSubmission(id).then(setSubmission);
        }
    };

    const  handleReceiptDeleted  =  (receiptId:  string)  =>  {
        setReceipts(receipts.filter(r  =>  r.receiptId  !==  receiptId));
        
        if  (id)  {
            submissionsAPI.getSubmission(id).then(setSubmission);
        }
    };

    const  handleReceiptUpdated  =  (updatedReceipt:  Receipt)  =>  {
        setReceipts(receipts.map(r  =>  
            r.receiptId  ===  updatedReceipt.receiptId  ?  updatedReceipt  :  r
        ));
    
        if  (id)  {
            submissionsAPI.getSubmission(id).then(setSubmission);
        }
    };

    const  handleSubmitForApproval  =  async  ()  =>  {
        if  (!id  ||  !signature.trim())  {
            return;
        }

        try  {
            setSubmitting(true);
            await  submissionsAPI.submitForApproval(id,  signature);
    
            await  fetchSubmissionData();
            showSuccess(submission?.isRejected  
                ?  'Submission  resubmitted  for  manager  review!'  
                :  'Submission  sent  for  manager  review!');
            setSubmitDialogOpen(false);
            setSignature('');
        }  catch  (err:  any)  {
            console.error('Error  submitting:',  err);
            
            //  ========================================
            //  NEW:  Check  for  NO_MANAGER_ASSIGNED  error
            //  ========================================
            if  (err.response?.data?.errorCode  ===  'NO_MANAGER_ASSIGNED')  {
    showError(
        'You  must  be  assigned  to  a  manager  before  submitting  expenses.  Please  contact  your  administrator  to  assign  you  to  a  manager.'
    );
}  else  {
    showError(err.response?.data?.error  ||  err.response?.data?.message  ||  'Failed  to  submit');
}
            //  ========================================
        }  finally  {
            setSubmitting(false);
            setSubmitDialogOpen(false);
            setSignature('');
        }
    };

    //  ========================================
    //  RENDER
    //  ========================================

    if  (loading)  {
        return  (
            <Box  display="flex"  justifyContent="center"  alignItems="center"  minHeight="400px">
                <CircularProgress  />
            </Box>
        );
    }

    if  (error  ||  !submission)  {
        return  (
            <Box>
                <Alert  severity="error">{error  ||  'Submission  not  found'}</Alert>
                <Button
                    startIcon={<BackIcon  />}
                    onClick={()  =>  navigate('/submissions')}
                    sx={{  mt:  2  }}
                >
                    Back  to  Submissions
                </Button>
            </Box>
        );
    }

    return  (
        <Box>
            {/*  Header  */}
            <Box  display="flex"  alignItems="center"  gap={2}  mb={3}>
                <Button
                    startIcon={<BackIcon  />}
                    onClick={()  =>  navigate('/submissions')}
                >
                    Back
                </Button>
                <Typography  variant="h4"  sx={{  flexGrow:  1  }}>
                    {submission.displayName}
                </Typography>
                {submission.wasResubmitted  &&  (
                    <Chip
                        label="Resubmitted"
                        color="info"
                        icon={<InfoIcon  />}
                        sx={{  mr:  1  }}
                    />
                )}
                {submission.programIsArchived  &&  (
                    <Chip
                        label="Program  Archived"
                        color="default"
                        sx={{  mr:  1  }}
                    />
                )}
                <Chip
                    label={getStatusLabel(submission.status)}
                    color={getStatusColor(submission.status)}
                />
            </Box>

            {/*  Rejection  Alert  */}
            {submission.isRejected  &&  (
                <Alert  
                    severity="error"  
                    icon={<WarningIcon  />}
                    sx={{  mb:  3  }}
                >
                    <Typography  variant="body1"  fontWeight="bold"  gutterBottom>
                        This  submission  was  rejected
                    </Typography>
                    <Typography  variant="body2">
                        {submission.rejectionReason}
                    </Typography>
                    <Typography  variant="body2"  sx={{  mt:  1  }}>
                        You  can  edit  the  receipts  and  resubmit  this  submission  for  approval.
                    </Typography>
                </Alert>
            )}

            {/*  Archive  Warning  */}
            {submission.programIsArchived  &&  submission.canEdit  &&  (
                <Alert  severity="warning"  icon={<WarningIcon  />}  sx={{  mb:  3  }}>
                    <Typography  variant="body2">
                        <strong>Note:</strong>  This  program  has  been  archived.  You  can  still  edit  and  submit  this  submission,
                        but  if  rejected,  you  will  not  be  able  to  view  or  resubmit  it.
                    </Typography>
                </Alert>
            )}

            {/*  Submission  Info  Card  */}
            <Card  sx={{  mb:  3  }}>
                <CardContent>
                    <Grid  container  spacing={3}>
                        <Grid  item  xs={12}  sm={6}>
                            <Typography  variant="body2"  color="text.secondary">
                                Period
                            </Typography>
                            <Typography  variant="h6">
                                {formatSubmissionPeriod(submission)}
                            </Typography>
                        </Grid>

                        <Grid  item  xs={12}  sm={6}>
                            <Typography  variant="body2"  color="text.secondary">
                                Total  Amount
                            </Typography>
                            <Typography  variant="h6"  color="primary.main">
                                ${submission.totalAmount.toFixed(2)}
                            </Typography>
                            {submission.amountToBePaid  &&  submission.amountToBePaid  <  submission.totalAmount  &&  (
                                <Typography  variant="caption"  color="warning.main"  display="block">
                                    Payment  capped  at  ${submission.amountToBePaid.toFixed(2)}  (program  allowance  limit)
                                </Typography>
                            )}
                        </Grid>

                        {submission.submissionNotes  &&  (
                            <Grid  item  xs={12}>
                                <Typography  variant="body2"  color="text.secondary"  gutterBottom>
                                    Submission  Notes
                                </Typography>
                                <Typography  variant="body1">
                                    {submission.submissionNotes}
                                </Typography>
                            </Grid>
                        )}

                        {/*  Show  Program  Categories  */}
                        {programCategories.length  >  0  &&  (
                            <Grid  item  xs={12}>
                                <Typography  variant="body2"  color="text.secondary"  gutterBottom>
                                    Approved  Expense  Categories
                                </Typography>
                                <Box  display="flex"  flexWrap="wrap"  gap={1}>
                                    {programCategories.map((category)  =>  (
                                        <Chip
                                            key={category}
                                            label={category}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                        />
                                    ))}
                                </Box>
                            </Grid>
                        )}

                        {submission.submittedDate  &&  (
                            <Grid  item  xs={12}>
                                <Divider  sx={{  my:  1  }}  />
                                <Typography  variant="body2"  color="text.secondary">
                                    Submitted  on  {dayjs(submission.submittedDate).format('MMMM  D,  YYYY')}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            {/*  Rejection  History  */}
            {submission.rejectionHistory  &&  submission.rejectionHistory.length  >  0  &&  (
                <Card  sx={{  mb:  3  }}>
                    <CardContent>
                        <Typography  variant="h6"  gutterBottom  sx={{  display:  'flex',  alignItems:  'center',  gap:  1  }}>
                            <RejectIcon  color="error"  />
                            Rejection  History
                        </Typography>
                        <Divider  sx={{  mb:  2  }}  />
                        {submission.rejectionHistory.map((rejection,  index)  =>  (
                            <Box  
                                key={index}  
                                sx={{  
                                    mb:  2,  
                                    pl:  2,  
                                    py:  1.5,
                                    borderLeft:  '4px  solid',  
                                    borderColor:  'error.main',
                                    backgroundColor:  'error.lighter',
                                    borderRadius:  '0  4px  4px  0'
                                }}
                            >
                                <Box  display="flex"  justifyContent="space-between"  alignItems="start">
                                    <Typography  variant="body2"  fontWeight="bold"  color="error.dark">
                                        Rejected  by  {rejection.rejectedByName}
                                    </Typography>
                                    <Typography  variant="caption"  color="text.secondary">
                                        {dayjs(rejection.date).format('MMM  D,  YYYY  h:mm  A')}
                                    </Typography>
                                </Box>
                                <Typography  variant="caption"  color="text.secondary"  display="block"  sx={{  mt:  0.5  }}>
                                    Previous  status:  {rejection.previousStatus}
                                </Typography>
                                <Typography  variant="body2"  sx={{  mt:  1  }}>
                                    <strong>Reason:</strong>  {rejection.reason}
                                </Typography>
                            </Box>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/*  Action  Buttons  */}
            {submission.canEdit  &&  (
                <Box  display="flex"  gap={2}  mb={3}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon  />}
                        onClick={()  =>  setAddReceiptOpen(true)}
                    >
                        Add  Receipt
                    </Button>
                    
                    {submission.canSubmit  &&  receipts.length  >  0  &&  (
                        <Button
                            variant="contained"
                            color={submission.isRejected  ?  "warning"  :  "success"}
                            startIcon={<SendIcon  />}
                            onClick={()  =>  setSubmitDialogOpen(true)}
                        >
                            {submission.isRejected  ?  'Resubmit  for  Approval'  :  'Submit  for  Approval'}
                        </Button>
                    )}
                </Box>
            )}

            {/*  Receipts  Section  */}
            <Typography  variant="h5"  gutterBottom  sx={{  mt:  4  }}>
                Receipts  ({receipts.length})
            </Typography>

            {receipts.length  ===  0  ?  (
                <Card>
                    <CardContent>
                        <Typography  variant="body1"  color="text.secondary"  align="center">
                            No  receipts  added  yet.  Click  "Add  Receipt"  to  get  started.
                        </Typography>
                    </CardContent>
                </Card>
            )  :  (
                <Grid  container  spacing={2}>
                    {receipts.map((receipt)  =>  (
                        <Grid  item  xs={12}  md={6}  key={receipt.receiptId}>
                            <ReceiptCard
                                receipt={receipt}
                                submissionId={submission.submissionId}
                                programCategories={programCategories}    
                                canEdit={submission.canEdit}
                                onDeleted={()  =>  handleReceiptDeleted(receipt.receiptId)}
                                onUpdated={handleReceiptUpdated}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/*  Add  Receipt  Dialog  */}
            <AddReceiptDialog
                open={addReceiptOpen}
                submissionId={submission.submissionId}
                programCategories={programCategories}    
                onClose={()  =>  setAddReceiptOpen(false)}
                onReceiptAdded={handleReceiptAdded}
            />

            {/*  Submit  for  Approval  Dialog  */}
            <Dialog  
                open={submitDialogOpen}  
                onClose={()  =>  !submitting  &&  setSubmitDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {submission.isRejected  ?  'Resubmit  for  Approval'  :  'Submit  for  Approval'}
                </DialogTitle>
                <DialogContent>
                    {submission.isRejected  &&  (
                        <Alert  severity="info"  sx={{  mb:  2  }}>
                            You  are  resubmitting  a  previously  rejected  submission.  Please  ensure  you  have
                            addressed  all  feedback  before  resubmitting.
                        </Alert>
                    )}
                    <Typography  variant="body2"  paragraph>
                        By  submitting,  you  certify  that  all  expenses  are  accurate  and  comply  with  company  policy.
                    </Typography>
                    <Typography  variant="body2"  paragraph  color="text.secondary">
                        <strong>Note:</strong>  Your  submission  will  be  sent  to  your  assigned  manager(s)  for  review.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Digital  Signature"
                        placeholder="Type  your  full  name"
                        value={signature}
                        onChange={(e)  =>  setSignature(e.target.value)}
                        sx={{  mt:  2  }}
                        autoFocus
                        disabled={submitting}
                        helperText={
                            <Box>
                                <Typography  variant="body2"  sx={{  fontWeight:  'bold',  mb:  0.5  }}>
                                    Please  type  your  name:  {user?.displayName  ||  'Your  Name'}
                                </Typography>
                                <Typography  variant="caption"  color="text.secondary">
                                    By  typing  your  name,  you  confirm  all  information  is  accurate
                                </Typography>
                            </Box>
                        }
                    />
                </DialogContent>
                <DialogActions>
                    <Button  
                        onClick={()  =>  setSubmitDialogOpen(false)}  
                        disabled={submitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmitForApproval}
                        disabled={!signature.trim()  ||  submitting}
                    >
                        {submitting  ?  'Submitting...'  :  submission.isRejected  ?  'Resubmit'  :  'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export  default  SubmissionDetail;